import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Grid, Box, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Typography } from '@mui/material';
import { Delete, Edit, Settings } from '@mui/icons-material';
import { toast } from 'react-toastify';
import MainCard from 'components/Cards/MainCard';
import { gridSpacing } from 'utils/constants';
import ApiHelper from 'utils/apiHelper';
import EditOrganizationDialog from './EditOrganizationDialog';
import ManageOrganizationDialog from './ManageOrganizationDialog';
import DeleteConfirmationDialog from 'components/Management/DeleteConfirmationDialog';

const ManagementOrganizations = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const manageOrganizationData = JSON.parse(localStorage.getItem('manageOrganization'));
    const [organizationsData, setOrganizationsData] = useState([]);
    const [manageOrganizationDialog, setManageOrganizationDialog] = useState(false);
    const [showEditOrganizationDialog, setShowEditOrganizationDialog] = useState(false);
    const [selectedOrganizationData, setSelectedOrganizationData] = useState();
    const [deleteOrganizationId, setDeleteOrganizationId] = useState();
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

    const getOrganizations = () => {
        setLoading(true);
        ApiHelper.get('/api/management/organizations/')
            .then((res) => {
                setOrganizationsData(res.data);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response.status !== 401) {
                    toast.error('Failed to get the organizations data');
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        getOrganizations();
    }, []);

    const handleEditOrganization = (data) => {
        setShowEditOrganizationDialog(true);
        setSelectedOrganizationData(data);
    };

    const handleManageOrganization = (data) => {
        setManageOrganizationDialog(true);
        setSelectedOrganizationData(data);
    };

    const handleCloseEditOrganization = () => {
        setShowEditOrganizationDialog(false);
        getOrganizations();
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteConfirmationDialog(true);
        setDeleteOrganizationId(id);
    };

    const handleClose = () => {
        setShowDeleteConfirmationDialog(false);
    };

    const handleDeleteOrganization = () => {
        ApiHelper.delete(`/api/management/organizations/${deleteOrganizationId}/`)
            .then(() => {
                toast.success('Organization was deleted successfully.');
                getOrganizations();
                handleClose();
            })
            .catch(() => {
                toast.error('Failed to delete a organization.');
                handleClose();
            });
    };

    const handleManageOrganizationData = () => {
        setLoading(true);
        ApiHelper.post('/api/management/manage_organization/', {
            manage_organization_id: selectedOrganizationData.orgid
        })
            .then(() => {
                localStorage.setItem('manageOrganization', JSON.stringify(selectedOrganizationData));
                setLoading(false);
                window.location.reload();
            })
            .catch((err) => {
                setLoading(false);
                if (err.response.data.message !== '') {
                    toast.error(`Failed! ${err.response.data.message}`);
                } else {
                    toast.error('Failed to manage the organization');
                }
            });
        setManageOrganizationDialog(false);
    };

    if (!organizationsData) {
        return null;
    } else {
        return (
            <MainCard
                title="Organization Management"
                cardHeaderShow={true}
                cardHeaderTitle={manageOrganizationData?.orgid ? manageOrganizationData?.name : userInfo.user.orgid.name}
            >
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        {/*<Grid*/}
                        {/*    container*/}
                        {/*    item*/}
                        {/*    sx={{*/}
                        {/*        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,*/}
                        {/*        padding: 1*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    <Grid item xs={12}>*/}
                        {/*        <Button*/}
                        {/*            sx={{ float: 'right', backgroundColor: '#FFFFFF', borderRadius: '6px' }}*/}
                        {/*            onClick={() => setRegisterOrganizationDialog(true)}*/}
                        {/*            startIcon={<Add />}*/}
                        {/*        >*/}
                        {/*            Register Organization*/}
                        {/*        </Button>*/}
                        {/*        {registerOrganizationDialog && (*/}
                        {/*            <RegisterOrganizationDialog onClose={() => setRegisterOrganizationDialog(false)} />*/}
                        {/*        )}*/}
                        {/*    </Grid>*/}
                        {/*</Grid>*/}
                        <Grid container spacing={gridSpacing}>
                            <Grid item lg={12} xs={12}>
                                {loading === true ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                        Please wait...
                                    </Typography>
                                ) : organizationsData.length === 0 ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center' }}>No Organizations Data</Typography>
                                ) : (
                                    <Table sx={{ minWidth: 700 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Organization Name</TableCell>
                                                <TableCell>Organization Description</TableCell>
                                                <TableCell>Organization Address</TableCell>
                                                <TableCell>Account Locked</TableCell>
                                                <TableCell>Auto Upgrade</TableCell>
                                                <TableCell>Baseline Set</TableCell>
                                                <TableCell>Options</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {organizationsData.map((data, key) => (
                                                <TableRow key={key}>
                                                    <TableCell>
                                                        {manageOrganizationData?.orgid === data.orgid
                                                            ? `${data.name} - managing`
                                                            : data.name}
                                                    </TableCell>
                                                    <TableCell>{data.description}</TableCell>
                                                    <TableCell>{data.address}</TableCell>
                                                    <TableCell>{data.locked === true ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell>{data.autoupgrade === true ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell>{data.baseline_set === true ? 'Yes' : 'No'}</TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            {userInfo.admin_permissions.root_admin && (
                                                                <IconButton size="small" onClick={() => handleManageOrganization(data)}>
                                                                    <Settings fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                            <IconButton size="small" onClick={() => handleEditOrganization(data)}>
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleDeleteConfirmation(data.orgid)}>
                                                                <Delete fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {manageOrganizationDialog && (
                                                <ManageOrganizationDialog
                                                    onSubmit={handleManageOrganizationData}
                                                    onClose={() => setManageOrganizationDialog(false)}
                                                />
                                            )}
                                            {showEditOrganizationDialog && (
                                                <EditOrganizationDialog
                                                    data={selectedOrganizationData}
                                                    onClose={handleCloseEditOrganization}
                                                />
                                            )}
                                            {showDeleteConfirmationDialog && (
                                                <DeleteConfirmationDialog
                                                    onClose={handleClose}
                                                    onYes={handleDeleteOrganization}
                                                    name="organization"
                                                />
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementOrganizations;
